import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Loader2, Search, X } from 'lucide-react'

import { useAppStore } from '@core/store'
import type { ModeloStep } from '@viewer/lib/step/modeloStep'
import { indiceDoEsquema, nomeDaClasse } from '@viewer/lib/step/esquemaIfc'
import { depoisDePintar, modeloEmTexto } from '@viewer/dados/modeloEmTexto'
import { AVISO, INPUT, NOTA } from '@viewer/dados/estilos'
import VistaEntidade, { LigacaoRef } from './VistaEntidade'

type Separador = 'entidade' | 'classes' | 'cabecalho'

const NOMES: Record<Separador, string> = {
  entidade: 'Entidade',
  classes: 'Classes',
  cabecalho: 'Cabeçalho',
}

const POR_PAGINA = 200

/** Contar as classes de um IFC de 150 MB leva uns 2 s: faz-se uma vez
 *  por modelo (o do painel é o original, que não se edita). */
const contagens = new WeakMap<ModeloStep, [string, number][]>()
function contagemDe(m: ModeloStep): [string, number][] {
  let c = contagens.get(m)
  if (!c) {
    c = m.contagemPorClasse()
    contagens.set(m, c)
  }
  return c
}

function ListaDaClasse({ m, classe, ir }: { m: ModeloStep; classe: string; ir: (id: number) => void }) {
  const [n, setN] = useState(POR_PAGINA)
  const ids = useMemo(() => m.idsDaClasse(classe), [m, classe])
  const esquema = m.esquema()
  return (
    <div className="flex flex-col pb-1 pl-4">
      {ids.slice(0, n).map((id) => (
        <LigacaoRef key={id} m={m} esquema={esquema} id={id} ir={ir} compacto />
      ))}
      {ids.length > n && (
        <button type="button" className="self-start text-[10px] text-[var(--color-accent)] hover:underline" onClick={() => setN(n + POR_PAGINA)}>
          Mostrar mais ({ids.length - n})
        </button>
      )}
    </div>
  )
}

function SeparadorClasses({
  m,
  filtro,
  setFiltro,
  aberta,
  setAberta,
  ir,
}: {
  m: ModeloStep
  filtro: string
  setFiltro: (f: string) => void
  aberta: string | null
  setAberta: (c: string | null) => void
  ir: (id: number) => void
}) {
  const contagem = contagemDe(m)
  const f = filtro.trim().toUpperCase()
  const linhas = f ? contagem.filter(([c]) => c.includes(f)) : contagem
  const total = contagem.reduce((n, [, k]) => n + k, 0)
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1">
      <input className={INPUT} placeholder="Filtrar classes (ex.: IfcRel, Property, Placement)" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
      <div className="text-[10px] text-[var(--color-text-muted)]">
        {contagem.length} classes, {total.toLocaleString('pt-PT')} entidades{f && `; ${linhas.length} classes com "${filtro.trim()}"`}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {linhas.map(([c, k]) => (
          <div key={c}>
            <button
              type="button"
              onClick={() => setAberta(aberta === c ? null : c)}
              className="flex w-full items-baseline gap-2 rounded px-1 py-0.5 text-left text-[11px] hover:bg-[var(--color-bg)]"
            >
              <span className="w-3 text-[var(--color-text-dim)]">{aberta === c ? '▾' : '▸'}</span>
              <span className="text-[var(--color-text)]">{nomeDaClasse(c)}</span>
              <span className="ml-auto font-mono text-[10px] text-[var(--color-text-muted)]">{k.toLocaleString('pt-PT')}</span>
            </button>
            {aberta === c && <ListaDaClasse m={m} classe={c} ir={ir} />}
          </div>
        ))}
      </div>
    </div>
  )
}

function SeparadorCabecalho({ m }: { m: ModeloStep }) {
  const esquema = m.esquema()
  const conhecido = indiceDoEsquema(esquema) !== null
  return (
    <div className="flex flex-col gap-2 text-[11px]">
      <div className="flex flex-wrap gap-x-3 text-[10px] text-[var(--color-text-muted)]">
        <span>
          Esquema declarado: <span className="font-mono text-[var(--color-text)]">{esquema ?? 'nenhum'}</span>
        </span>
        <span>Linhas na secção DATA: {m.linhas.size.toLocaleString('pt-PT')}</span>
      </div>
      {!conhecido && (
        <div className={AVISO}>
          O web-ifc não traz este esquema: as entidades mostram-se com os argumentos numerados, sem nomes de atributos.
        </div>
      )}
      <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-all rounded border border-[var(--color-border)] bg-[var(--color-bg)] p-1.5 font-mono text-[10px] text-[var(--color-text)]">
        {m.cabecalho() || '(sem secção HEADER)'}
      </pre>
    </div>
  )
}

/** Interpreta o que se escreveu na pesquisa. */
function pesquisar(m: ModeloStep, q: string): { id: number } | { classe: string } | { filtro: string } | { erro: string } {
  const t = q.trim()
  if (!t) return { erro: 'Escreve um #id, uma GlobalId ou o nome de uma classe.' }
  const n = /^#?\s*(\d+)$/.exec(t)
  if (n) {
    const id = parseInt(n[1], 10)
    return m.existe(id) ? { id } : { erro: `Não há nenhuma entidade #${id} neste ficheiro.` }
  }
  if (/^[0-9A-Za-z_$]{22}$/.test(t)) {
    const id = m.porGuid(t)
    if (id !== null) return { id }
  }
  const c = t.toUpperCase()
  if (m.idsDaClasse(c).length > 0) return { classe: c }
  return { filtro: t }
}

/** Estrutura IFC (modo avançado): qualquer entidade do ficheiro, como
 *  está escrita. Os atributos têm o nome que o esquema declarado no
 *  cabeçalho lhes dá; as referências levam à entidade apontada; e
 *  "Referenciado por" mostra quem aponta para esta, com o nome do
 *  atributo inverso quando o esquema o declara. Lê o texto do IFC (não
 *  o que o motor de visualização guardou), por isso mostra tudo, também
 *  o que não tem geometria. */
export default function PainelEstrutura({ aoFechar }: { aoFechar: () => void }) {
  const modelos = useAppStore((s) => s.models)
  const activo = useAppStore((s) => s.activeModelId)
  const selecao = useAppStore((s) => s.selection)
  const alvo = useAppStore((s) => s.estruturaAlvo)
  const [modelId, setModelId] = useState<string | null>(activo ?? modelos[0]?.id ?? null)
  const [carregado, setCarregado] = useState<{ modelId: string; m: ModeloStep | null; erro: string | null } | null>(null)
  const [sep, setSep] = useState<Separador>('entidade')
  const [historia, setHistoria] = useState<number[]>([])
  const [pos, setPos] = useState(-1)
  const [seguir, setSeguir] = useState(true)
  const [q, setQ] = useState('')
  const [filtro, setFiltro] = useState('')
  const [aberta, setAberta] = useState<string | null>(null)

  const [prevModel, setPrevModel] = useState(modelId)
  const [alvoVisto, setAlvoVisto] = useState(0)
  const [selVista, setSelVista] = useState('')

  // O modelo escolhido desapareceu (fechado): passa ao activo.
  if (modelId && !modelos.some((x) => x.id === modelId)) setModelId(activo ?? modelos[0]?.id ?? null)
  // Outro modelo: a história era do anterior.
  if (prevModel !== modelId) {
    setPrevModel(modelId)
    setHistoria([])
    setPos(-1)
  }

  useEffect(() => {
    let vivo = true
    if (!modelId) return
    // Indexar um IFC grande leva uns segundos: deixa pintar o indicador.
    void depoisDePintar().then(() => {
      if (!vivo) return
      try {
        const novo = modeloEmTexto(modelId)
        setCarregado({
          modelId,
          m: novo,
          erro: novo ? null : 'Este modelo não tem o texto do IFC em memória (abriu-se de um .frag?). Abre o .ifc para ver a estrutura.',
        })
      } catch (e) {
        setCarregado({ modelId, m: null, erro: e instanceof Error ? e.message : String(e) })
      }
    })
    return () => {
      vivo = false
    }
  }, [modelId])

  const pronto = carregado && carregado.modelId === modelId ? carregado : null
  const m = pronto?.m ?? null
  const erro = pronto?.erro ?? null
  const aLer = !!modelId && !pronto
  const id = pos >= 0 ? historia[pos] : null

  const ir = (novo: number) => {
    setSep('entidade')
    if (novo === id) return
    const h = [...historia.slice(0, pos + 1), novo].slice(-200)
    setHistoria(h)
    setPos(h.length - 1)
  }

  // Pedidos de fora (o painel Propriedades, por exemplo).
  if (alvo && alvo.n !== alvoVisto) {
    if (alvo.modelId !== modelId) setModelId(alvo.modelId)
    else if (m) {
      setAlvoVisto(alvo.n)
      if (m.existe(alvo.id)) ir(alvo.id)
    }
  }

  // Seguir a selecção do 3D.
  const chaveSel = selecao ? `${selecao.modelId}:${selecao.localId}` : ''
  if (seguir && m && chaveSel !== selVista) {
    setSelVista(chaveSel)
    if (selecao && selecao.modelId === modelId && m.existe(selecao.localId)) ir(selecao.localId)
  }

  const procurar = () => {
    if (!m) return
    const r = pesquisar(m, q)
    if ('id' in r) ir(r.id)
    else if ('classe' in r) {
      setSep('classes')
      setFiltro(nomeDaClasse(r.classe))
      setAberta(r.classe)
    } else if ('filtro' in r) {
      setSep('classes')
      setFiltro(r.filtro)
    } else useAppStore.getState().pushToast(r.erro, { type: 'error' })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-1 border-b border-[var(--color-border)] px-2 py-1">
        <span className="text-xs font-medium text-[var(--color-text)]">Estrutura IFC</span>
        <span className="rounded bg-[var(--color-accent-bg)] px-1 text-[9px] text-[var(--color-accent)]">avançado</span>
        {modelos.length > 1 ? (
          <select
            className="ml-1 max-w-[220px] truncate rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 text-[10px] text-[var(--color-text)]"
            value={modelId ?? ''}
            onChange={(e) => setModelId(e.target.value)}
          >
            {modelos.map((x) => (
              <option key={x.id} value={x.id}>{x.fileName}</option>
            ))}
          </select>
        ) : (
          modelos[0] && <span className="ml-1 truncate text-[10px] text-[var(--color-text-muted)]">{modelos[0].fileName}</span>
        )}
        <button type="button" onClick={aoFechar} className="ml-auto rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]" title="Fechar">
          <X size={14} />
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-[var(--color-border)] px-2 py-1">
        <button
          type="button"
          className="rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
          disabled={pos <= 0}
          onClick={() => { setPos(pos - 1); setSep('entidade') }}
          title="Para trás"
        >
          <ArrowLeft size={14} />
        </button>
        <button
          type="button"
          className="rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
          disabled={pos >= historia.length - 1}
          onClick={() => { setPos(pos + 1); setSep('entidade') }}
          title="Para a frente"
        >
          <ArrowRight size={14} />
        </button>
        <form
          className="flex flex-1 items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault()
            procurar()
          }}
        >
          <input className={INPUT} placeholder="#id, GlobalId ou classe (ex.: #120, IfcWall, 2O2Fr$t4X7Zf8NOew3FLOH)" value={q} onChange={(e) => setQ(e.target.value)} disabled={!m} />
          <button type="submit" className="rounded p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]" title="Procurar" disabled={!m}>
            <Search size={13} />
          </button>
        </form>
        <label className="flex shrink-0 items-center gap-1 text-[10px] text-[var(--color-text-muted)]" title="Mostrar o elemento seleccionado no 3D">
          <input type="checkbox" checked={seguir} onChange={(e) => setSeguir(e.target.checked)} />
          Seguir a selecção
        </label>
      </div>

      <div className="flex gap-0.5 border-b border-[var(--color-border)] px-2 pt-1">
        {(Object.keys(NOMES) as Separador[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSep(s)}
            className={`rounded-t px-2 py-1 text-[11px] ${
              sep === s
                ? 'border border-b-0 border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {NOMES[s]}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-auto p-3">
        {!modelId && <p className={NOTA}>Abre um IFC para ver a estrutura.</p>}
        {aLer && (
          <div className="flex items-center gap-1 text-[11px] text-[var(--color-text-muted)]">
            <Loader2 size={12} className="animate-spin" /> A indexar o ficheiro...
          </div>
        )}
        {erro && <div className={AVISO}>{erro}</div>}
        {m && sep === 'entidade' && id === null && (
          <p className={NOTA}>
            Selecciona um elemento no 3D, procura um #id, uma GlobalId ou uma classe, ou escolhe uma entidade no separador
            Classes. Cada referência leva à entidade apontada, e "Referenciado por" mostra quem aponta para ela.
          </p>
        )}
        {m && modelId && sep === 'entidade' && id !== null && <VistaEntidade m={m} modelId={modelId} id={id} ir={ir} />}
        {m && sep === 'classes' && (
          <SeparadorClasses m={m} filtro={filtro} setFiltro={setFiltro} aberta={aberta} setAberta={setAberta} ir={ir} />
        )}
        {m && sep === 'cabecalho' && <SeparadorCabecalho m={m} />}
      </div>
      <p className="border-t border-[var(--color-border)] px-3 py-1 text-[9px] text-[var(--color-text-dim)]">
        Nomes de atributos, inversos e herança: tabelas do esquema IFC incluídas no web-ifc (MPL-2.0), geradas dos EXPRESS
        da buildingSMART. Lido do texto do ficheiro original, sem as alterações pendentes.
      </p>
    </div>
  )
}
